/**
 * ARK Drops RS — How-to section: lazy videos, sequential playback, dim overlay.
 */
(function () {
  const ROOT_SELECTOR = '[data-ark-how-to]';
  const TOUCH_UI_MQ = '(max-width: 1024px), (hover: none) and (pointer: coarse)';

  class ArkHowToSequence {
    constructor(root) {
      this.root = root;
      this.steps = Array.from(root.querySelectorAll('[data-ark-how-to-step]'));
      this.videos = this.steps.map((step) => step.querySelector('[data-ark-how-to-video]'));
      this.medias = this.steps.map((step) => step.querySelector('[data-ark-how-to-media]'));

      this.mode = 'outOfView';
      this.activeIndex = 0;
      this.hoverIndex = -1;
      this.inView = false;
      this.savedState = null;
      this.reducedMotion = false;
      this.loaded = new Set();
      this.isTouchUI = false;
      this._touchUIMq = null;

      try {
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch {
        /* matchMedia unsupported */
      }

      this._updateTouchUI();
      try {
        this._touchUIMq = window.matchMedia(TOUCH_UI_MQ);
        this._touchUIMq.addEventListener('change', () => this._updateTouchUI());
      } catch {
        /* matchMedia unsupported */
      }

      this._onEnded = this._handleEnded.bind(this);
      this._onIntersect = this._handleIntersect.bind(this);

      if (!this.videos.some(Boolean)) {
        return;
      }

      this._bind();
      this.observer = new IntersectionObserver(this._onIntersect, {
        threshold: 0.25,
        rootMargin: '120px 0px',
      });
      this.observer.observe(this.root);
    }

    _updateTouchUI() {
      try {
        this.isTouchUI = window.matchMedia(TOUCH_UI_MQ).matches;
      } catch {
        this.isTouchUI = false;
      }
    }

    _bind() {
      this.steps.forEach((_, i) => this.setDimmed(i, true));

      this.videos.forEach((video) => {
        if (!video) return;
        video.loop = false;
        video.removeAttribute('src');
        video.addEventListener('ended', this._onEnded);
      });

      if (this.isTouchUI) {
        this.steps.forEach((step, i) => {
          step.addEventListener('click', () => this.onStepTap(i));
        });
      } else {
        this.steps.forEach((step, i) => {
          step.addEventListener('pointerenter', () => this.onHoverEnter(i));
          step.addEventListener('pointerleave', () => this.onHoverLeave(i));
        });
      }
    }

    setDimmed(index, dimmed) {
      const media = this.medias[index];
      const video = this.videos[index];

      if (media) {
        media.classList.toggle('is-dimmed', dimmed);
        if (dimmed) {
          media.setAttribute('data-ark-how-to-dimmed', 'true');
        } else {
          media.removeAttribute('data-ark-how-to-dimmed');
        }
      }

      if (video && dimmed) {
        video.pause();
        video.loop = false;
      }
    }

    loadVideo(index) {
      const video = this.videos[index];
      if (!video || this.loaded.has(index)) {
        return Promise.resolve(video);
      }

      const dataSrc = video.getAttribute('data-src');
      if (!dataSrc) {
        return Promise.resolve(video);
      }

      if (!video.getAttribute('src')) {
        video.setAttribute('src', dataSrc);
      }

      this.loaded.add(index);

      return new Promise(function (resolve) {
        if (video.readyState >= 2) {
          resolve(video);
          return;
        }

        function onReady() {
          video.removeEventListener('loadeddata', onReady);
          video.removeEventListener('error', onReady);
          resolve(video);
        }

        video.addEventListener('loadeddata', onReady);
        video.addEventListener('error', onReady);
        video.load();
      });
    }

    pauseAll() {
      this.videos.forEach((v) => {
        if (v) v.pause();
      });
    }

    resetVideo(index) {
      const v = this.videos[index];
      if (v && v.getAttribute('src')) {
        v.currentTime = 0;
      }
    }

    safePlay(video) {
      if (!video || !video.getAttribute('src')) return;
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {});
      }
    }

    playOnce(index) {
      const video = this.videos[index];
      if (!video || !this.inView) return;

      const self = this;

      this.loadVideo(index).then(function () {
        if (!self.inView) return;

        self.activeIndex = index;
        self.mode = 'sequence';
        self.hoverIndex = -1;

        self.videos.forEach((v, i) => {
          if (!v) return;
          if (i !== index) {
            v.pause();
            v.loop = false;
          }
        });

        self.steps.forEach((_, i) => {
          self.setDimmed(i, i !== index);
        });

        video.loop = false;
        self.safePlay(video);

        const next = (index + 1) % self.videos.length;
        self.loadVideo(next);

        const peekNext = (index + 2) % self.videos.length;
        if (peekNext !== index) {
          self.loadVideo(peekNext);
        }
      });
    }

    playLoop(index) {
      const video = this.videos[index];
      if (!video || !this.inView) return;

      const self = this;

      this.loadVideo(index).then(function () {
        if (!self.inView) return;

        self.activeIndex = index;
        self.hoverIndex = index;
        self.mode = 'hover';

        self.videos.forEach((v, i) => {
          if (!v) return;
          if (i !== index) {
            v.pause();
            v.loop = false;
          }
        });

        self.steps.forEach((_, i) => {
          self.setDimmed(i, i !== index);
        });

        video.loop = true;
        self.safePlay(video);
      });
    }

    advance() {
      if (this.mode !== 'sequence' || !this.inView) return;

      const current = this.activeIndex;
      const video = this.videos[current];
      if (video) {
        video.pause();
        video.loop = false;
      }
      this.setDimmed(current, true);

      const next = (current + 1) % this.videos.length;
      this.resetVideo(next);
      this.playOnce(next);
    }

    _handleEnded(e) {
      const video = e.target;
      const index = this.videos.indexOf(video);
      if (index === -1) return;
      if (this.mode !== 'sequence') return;
      if (index !== this.activeIndex) return;
      if (!this.inView) return;

      this.advance();
    }

    _handleIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;

      if (entry.isIntersecting) {
        this.enterViewport();
      } else {
        this.leaveViewport();
      }
    }

    enterViewport() {
      if (this.inView) return;
      this.inView = true;

      if (this.reducedMotion) {
        const self = this;
        this.loadVideo(0).then(function () {
          self.steps.forEach((_, i) => self.setDimmed(i, i !== 0));
        });
        return;
      }

      if (this.savedState) {
        const state = this.savedState;
        this.savedState = null;
        const idx = state.activeIndex;
        const video = this.videos[idx];

        if (!video) {
          this.playOnce(0);
          return;
        }

        const self = this;

        this.loadVideo(idx).then(function () {
          if (!self.inView) return;

          video.currentTime = state.currentTime;

          if (state.mode === 'hover' && !self.isTouchUI) {
            self.playLoop(idx);
          } else {
            self.mode = 'sequence';
            self.activeIndex = idx;
            self.steps.forEach((_, i) => self.setDimmed(i, i !== idx));
            video.loop = false;
            self.safePlay(video);
          }
        });
        return;
      }

      this.playOnce(0);
    }

    leaveViewport() {
      if (!this.inView) return;
      this.inView = false;

      const idx = this.mode === 'hover' ? this.hoverIndex : this.activeIndex;
      const video = this.videos[idx];
      const currentTime = video ? video.currentTime : 0;

      this.savedState = {
        mode: this.mode,
        activeIndex: idx,
        currentTime: currentTime,
      };

      this.pauseAll();
      this.videos.forEach((v) => {
        if (v) v.loop = false;
      });
      this.steps.forEach((_, i) => this.setDimmed(i, true));
    }

    onStepTap(index) {
      if (!this.inView || this.reducedMotion) return;

      const video = this.videos[index];
      if (
        index === this.activeIndex &&
        this.mode === 'sequence' &&
        video &&
        !video.paused &&
        !video.ended
      ) {
        return;
      }

      this.resetVideo(index);
      this.playOnce(index);
    }

    onHoverEnter(index) {
      if (!this.inView || this.reducedMotion || this.isTouchUI) return;

      this.videos.forEach((v, i) => {
        if (!v) return;
        v.pause();
        if (i !== index) v.loop = false;
      });

      this.resetVideo(index);
      this.playLoop(index);
    }

    onHoverLeave(index) {
      if (!this.inView || this.reducedMotion || this.isTouchUI) return;
      if (this.mode !== 'hover') return;
      if (this.hoverIndex !== index) return;

      const video = this.videos[index];
      if (video) {
        video.pause();
        video.loop = false;
      }
      this.setDimmed(index, true);

      const next = (index + 1) % this.videos.length;
      this.hoverIndex = -1;
      this.resetVideo(next);
      this.playOnce(next);
    }
  }

  function initRoot(root) {
    if (root.dataset.arkHowToInit === 'true') return;
    root.dataset.arkHowToInit = 'true';
    new ArkHowToSequence(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
